// StudioTopBar — the bar across the Studio (pen "Studio · Top bar", placed
// by pen Q76: the top bar says where you are and holds what acts on the
// whole): the app menu's toggle, the breadcrumb to the page shown (every
// level above it a way back), then on the right Undo and Redo of the
// document shown (its editor's history; elsewhere there is nothing to undo,
// so they rest disabled), History (what happened, the History surface) and
// Export. Facts about the page (its kind, its sources) sit with the page
// (PageFacts.tsx). The way back to all projects is the rail's. Pure.

export { StudioTopBar };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import type { DeskCommand } from "../Studio/studioDesk.js";
import { PageCrumbs } from "./PageCrumbs.js";
import type { PageCrumb } from "./studioPages.js";

// Undo and Redo of the document shown.
type TopBarHistory = {
    readonly canUndo: boolean;
    readonly canRedo: boolean;
    readonly onUndo: () => void;
    readonly onRedo: () => void;
};

const StudioTopBar = ({
    crumbs,
    here,
    menuShown,
    online,
    history,
    onOpen,
    onToggleMenu,
    onHistory,
    onExport,
}: {
    // The levels above the page shown, the outermost first.
    readonly crumbs: readonly PageCrumb[];
    // The page shown, by name.
    readonly here: string;
    readonly menuShown: boolean;
    readonly online: boolean;
    // Absent: nothing shown that has an undo history.
    readonly history: TopBarHistory | undefined;
    // A level of the breadcrumb opened.
    readonly onOpen: (commands: readonly DeskCommand[]) => void;
    readonly onToggleMenu: () => void;
    readonly onHistory: () => void;
    // Export the whole content (absent: no one thing to export).
    readonly onExport: (() => void) | undefined;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <header className="bilda-pages__top">
            <IconButton
                label={menuShown ? t`Hide the menu` : t`Show the menu`}
                variant="ghost"
                size="sm"
                onClick={onToggleMenu}
            >
                <Icon
                    name={menuShown ? "panel-left-close" : "panel-left-open"}
                />
            </IconButton>
            <PageCrumbs crumbs={crumbs} here={here} onOpen={onOpen} />
            <span className="bilda-pages__top-spacer" />
            <div className="bilda-pages__top-history">
                <IconButton
                    label={t`Undo`}
                    variant="ghost"
                    size="md"
                    disabled={history?.canUndo !== true}
                    onClick={history?.onUndo}
                >
                    <Icon name="undo" />
                </IconButton>
                <IconButton
                    label={t`Redo`}
                    variant="ghost"
                    size="md"
                    disabled={history?.canRedo !== true}
                    onClick={history?.onRedo}
                >
                    <Icon name="redo" />
                </IconButton>
                <IconButton
                    label={t`History`}
                    variant="ghost"
                    size="md"
                    onClick={onHistory}
                >
                    <Icon name="history" />
                </IconButton>
            </div>
            {onExport === undefined ? null : (
                <Button variant="primary" disabled={!online} onClick={onExport}>
                    {t`Export`}
                </Button>
            )}
        </header>
    );
};
